import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  Tooltip,
  Collapse,
  ListItemButton,
  Container,
  
  
  Fade,
  Chip,
  alpha,
  Backdrop,
  LinearProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ImportExport as ImportIcon,
  Business,
  CompareArrows,
  Category,
  SupervisorAccount,
  Support as SupportIcon,
  ExpandLess,
  ExpandMore,
  Group as UsersIcon,
  ExitToApp as LogoutIcon,
  Assessment as ReportIcon,
  AccountBox,
  TrackChanges as MetasIcon,
  GroupWork as ComerciaisSegIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import useAutoLogout from './useAutoLogout';

const drawerWidth = 280;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    }),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
    },
  }),
);

const AppBarStyled = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    backdropFilter: 'blur(8px)',
    backgroundColor: alpha(theme.palette.background.default, 0.8),
    color: theme.palette.text.primary,
    boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

// Estilizar o item do menu principal
const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0.5, 0),
  borderRadius: theme.shape.borderRadius,
  ...(active === 'true' && {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

// Estilizar o item do submenu
const StyledSubListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0.3, 0),
  paddingLeft: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  ...(active === 'true' && {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

// Removido: Badge de notificações

const Layout = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(location.pathname.startsWith('/admin'));
  
  const [loading, setLoading] = useState(false);
  
  
  // Obter informações do usuário do localStorage com o novo formato
  const userData = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const isAdmin = userData?.is_admin;
  const username = userData?.username || 'Usuário';

  useAutoLogout();

  useEffect(() => {
    // Em dispositivos móveis, fechar o drawer após a navegação
    if (isMobile) {
      setOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Simular carregamento ao navegar
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAdminMenuToggle = () => setAdminMenuOpen(!adminMenuOpen);
  

  const handleLogout = () => {
    handleMenuClose();
    setLoading(true);
    
    setTimeout(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }, 500);
  };

  const menuItems = [
    {
      text: 'Importação',
      icon: <ImportIcon />,
      path: '/importacao',
      description: 'Importar novos arquivos',
      show: true,
    },
    {
      text: 'Relatório de Resultados',
      icon: <ReportIcon />,
      path: '/relatorio-resultados',
      description: 'Visualizar relatório consolidado de resultados',
      show: true,
    }
  ];

  const adminMenuItems = [
    {
      text: 'Usuários',
      icon: <UsersIcon />,
      path: '/admin/usuarios',
      description: 'Gerenciar usuários do sistema',
    },
    {
      text: 'Empresas',
      icon: <Business />,
      path: '/admin/empresas',
      description: 'Gerenciar cadastro de empresas',
    },
    {
      text: 'Seguradoras',
      icon: <ReportIcon />,
      path: '/admin/seguradoras',
      description: 'Gerenciar seguradoras do sistema',
    },
    {
      text: 'Mapeamentos',
      icon: <CompareArrows />,
      path: '/admin/empresa-seguradora',
      description: 'Gerenciar mapeamentos empresa-seguradora',
    },
    // De/Para desativado: migração concluída para modelo relacional
    // {
    //   text: 'De/Para',
    //   icon: <CompareArrows />,
    //   path: '/admin/depara',
    //   description: 'Mapeamentos Empresa-Seguradora',
    // },
    {
      text: 'Categorias',
      icon: <Category />,
      path: '/admin/categorias',
      description: 'Gerenciar categorias',
    },
    {
      text: 'Responsáveis',
      icon: <SupervisorAccount />,
      path: '/admin/responsaveis',
      description: 'Gerenciar responsáveis',
    },
    {
      text: 'Comercial por Corretora',
      icon: <AccountBox />,
      path: '/admin/comerciais-corretoras',
      description: 'Gerenciar comerciais vinculados por corretora (empresa)',
    },
    {
      text: 'Comerciais por Seguradora',
      icon: <ComerciaisSegIcon />,
      path: '/admin/comerciais-seguradoras',
      description: 'Gerenciar comerciais vinculados por seguradora',
    },
    {
      text: 'Tipos de Atendimento',
      icon: <SupportIcon />,
      path: '/admin/tipos-atendimento',
      description: 'Gerenciar tipos de atendimento',
    },
    {
      text: 'Metas',
      icon: <MetasIcon />,
      path: '/admin/metas',
      description: 'Cadastro e gestão de metas por responsável e seguradora',
    },
  ];

  

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Barra de progresso durante carregamento */}
      {loading && (
        <LinearProgress 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 9999,
            height: 3 
          }} 
        />
      )}
      
      {/* Menu superior */}
      <AppBarStyled position="fixed" open={open} elevation={0}>
        <Container maxWidth={false}>
          <Toolbar disableGutters>
            <IconButton
              color="inherit"
              aria-label="abrir menu"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(open && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Logo em dispositivos móveis quando o menu está fechado */}
            {isMobile && !open && (
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  alignItems: 'center' 
                }}
              >
                <ImportIcon sx={{ mr: 1 }} color="primary" />
                Sistema
              </Typography>
            )}
            
            <Box sx={{ flexGrow: 1 }} />
            
            {/* Ícones de ação na barra superior */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Menu de usuário */}
              <Tooltip title="Menu do usuário">
                <IconButton
                  onClick={handleMenuOpen}
                  size="small"
                  sx={{ ml: 1 }}
                  aria-controls="user-menu"
                  aria-haspopup="true"
                >
                  <Avatar sx={{ 
                    bgcolor: theme.palette.primary.main,
                    width: 35,
                    height: 35,
                    fontWeight: 'bold'
                  }}>
                    {username.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </Container>
      </AppBarStyled>
      
      
      {/* Menu do usuário */}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar 
              sx={{ 
                bgcolor: theme.palette.primary.main, 
                width: 32, 
                height: 32,
                mr: 1.5
              }}
            >
              {username.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2">{username}</Typography>
              <Typography variant="caption" color="text.secondary">
                {isAdmin ? 'Administrador' : 'Usuário'}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        
        
        <Divider />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            Sair
          </ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Menu lateral */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        <DrawerHeader sx={{ 
          px: 3, 
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImportIcon color="primary" fontSize="large" />
            <Typography variant="h6" color="primary" fontWeight="bold">
              Sistema Admin
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        
        <Divider />
        
        {/* Menu do usuário no drawer */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.primary.main, 
              width: 40, 
              height: 40,
              mr: 2
            }}
          >
            {username.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">{username}</Typography>
            <Chip 
              label={isAdmin ? "Administrador" : "Usuário"} 
              size="small" 
              color={isAdmin ? "primary" : "default"}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Box>
        </Box>
        
        <Divider sx={{ mt: 1, mb: 2 }} />
        
        {/* Itens de menu */}
        <List sx={{ px: 2, py: 1 }}>
          {menuItems.map((item) => (
            <StyledListItemButton
              key={item.text}
              active={(location.pathname === item.path).toString()}
              onClick={() => navigate(item.path)}
              sx={{ mb: 0.5 }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ fontSize: '0.95rem' }}
              />
            </StyledListItemButton>
          ))}

          {isAdmin && (
            <>
              <StyledListItemButton
                onClick={handleAdminMenuToggle}
                active={location.pathname.startsWith('/admin').toString()}
              >
                <ListItemIcon
                  sx={{ minWidth: 40 }}
                >
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Administração" 
                  primaryTypographyProps={{ fontSize: '0.95rem' }}
                />
                {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </StyledListItemButton>
              <Collapse in={adminMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ mt: 1 }}>
                  {adminMenuItems.map((item) => (
                    <StyledSubListItemButton
                      key={item.text}
                      active={(location.pathname === item.path).toString()}
                      onClick={() => navigate(item.path)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 32, fontSize: '0.9rem' }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                      />
                    </StyledSubListItemButton>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
        
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Rodapé do menu */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" component="div" align="center">
            © {new Date().getFullYear()} Sistema Admin
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" align="center">
            v1.0.0
          </Typography>
        </Box>
      </Drawer>

      <Main open={open}>
        <DrawerHeader />
        <Fade in={!loading}>
          <Box>{children}</Box>
        </Fade>
      </Main>
      
      {/* Background escurecido para mobile quando menu está aberto */}
      {isMobile && open && (
        <Backdrop
          sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
          open={open}
          onClick={handleDrawerClose}
        />
      )}
    </Box>
  );
};

export default Layout; 